import html_default from "./template.default.html?raw";
const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("default" == app_theme) {
  html = html_default;
}
import { computed, inject, ref, onMounted, onUnmounted } from "vue";
import TsmSpinner from "../TsmSpinner/index.ts";
import LoginCode from "../LoginCode/index.ts";
import type { UserService } from "../../application/services/UserService.ts";
import type { VideoCollectionDto } from "../../shared/VideoCollectionDto.ts";
import type { VideoDto } from "../../shared/VideoDto.ts";
import type { VideoService } from "../../application/services/VideoService.ts";

// Videobereich fuer Kunden: eigener Kopf mit Anmeldung/Abmeldung, Kollektionen als Abschnitte mit Videokarten,
// Wiedergabe direkt in der Karte (JW Player). Ohne Login zeigt der Bereich die Code-Maske.
export default {
  template: html,
  components: { TsmSpinner, LoginCode },
  setup() {
    const userService: UserService | undefined = inject("userService");
    const videoService: VideoService | undefined = inject("videoService");
    const error = ref("");
    const playing = ref<string>("");
    const busy = ref(false);
    const collections = ref<VideoCollectionDto[]>([]);
    const initialized = ref(false);
    const isLoggedIn = ref(false);
    const customerName = ref("");
    const filter = ref("");
    const activeCollection = ref("");
    let heartbeatInterval: number | undefined;

    const updateLoggedInValue = async function () {
      if (!userService) {
        isLoggedIn.value = false;
        return;
      }
      try {
        const info = await userService.getUserInfo();
        isLoggedIn.value = info?.isLoggedIn ?? false;
        customerName.value = info?.customerName ?? "";
      } catch {
        isLoggedIn.value = false;
      }
    };

    const loadVideos = async function () {
      if (!videoService || !isLoggedIn.value) {
        collections.value = [];
        return;
      }
      busy.value = true;
      try {
        collections.value = (await videoService.getAllVideoCollections()).filter((c) => c.videos && c.videos.length > 0);
      } catch (err) {
        error.value = String(err);
      } finally {
        busy.value = false;
      }
    };

    const totalVideos = computed(() => collections.value.reduce((n, c) => n + c.videos.length, 0));

    const visibleCollections = computed(function () {
      const q = filter.value.trim().toLowerCase();
      return collections.value
        .filter((c) => activeCollection.value == "" || c.name == activeCollection.value)
        .map((c) => ({
          name: c.name,
          videos: q == "" ? c.videos : c.videos.filter((v) => `${v.title ?? ""} ${v.description ?? ""}`.toLowerCase().includes(q)),
        }))
        .filter((c) => c.videos.length > 0);
    });

    const thumb = (v: VideoDto) => `https://content.jwplatform.com/thumbs/${v.code}-640.jpg`;
    const playerUrl = (v: VideoDto) => `https://content.jwplatform.com/players/${v.code}-${v.playerId}.html?autostart=true`;
    const play = (v: VideoDto) => (playing.value = v.code);
    const stop = () => (playing.value = "");

    const onLoggedIn = async function () {
      error.value = "";
      await updateLoggedInValue();
      await loadVideos();
    };

    const logout = async function () {
      if (userService) {
        const url = await userService.getLogoutUrl();
        await userService.logout();
        location.href = url;
      }
    };

    onMounted(async function () {
      await updateLoggedInValue();
      await loadVideos();
      initialized.value = true;
      heartbeatInterval = window.setInterval(async function () {
        const before = isLoggedIn.value;
        await updateLoggedInValue();
        if (before != isLoggedIn.value) {
          await loadVideos();
        }
      }, 60000);
    });

    onUnmounted(() => {
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }
    });

    return {
      error,
      busy,
      initialized,
      isLoggedIn,
      customerName,
      collections,
      visibleCollections,
      totalVideos,
      filter,
      activeCollection,
      playing,
      thumb,
      playerUrl,
      play,
      stop,
      onLoggedIn,
      logout,
    };
  },
};
